import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaExpand,
  FaCompress,
  FaRedoAlt,
  FaUndoAlt,
  FaRegWindowRestore,
  FaCog,
  FaSpinner,
} from 'react-icons/fa';
import { IoMdSpeedometer } from 'react-icons/io';
import { MdBrightnessMedium } from 'react-icons/md';
import { TbRepeatOff, TbPlayerSkipForward } from 'react-icons/tb';
import { BiSkipNext, BiSkipPrevious } from 'react-icons/bi';
import { MdOutlineRepeatOne } from 'react-icons/md';
import './EnhancedVideoPlayer.css';

const EnhancedVideoPlayer = forwardRef(({
  videoUrl,
  onEnded,
  onTimeUpdate,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  initialPosition = 0,
  size = 'default',
  onPlayStart,
  onMetadataLoaded,
  autoPlay = false,
  onFirstPlay: _onFirstPlay,
}, ref) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [brightness, setBrightness] = useState(100);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState(null);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [showBrightnessSlider, setShowBrightnessSlider] = useState(false);
  const [showKeyboardHints, setShowKeyboardHints] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loopMode, setLoopMode] = useState('next');
  const [showLoopMessage, setShowLoopMessage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [wasPlaying, setWasPlaying] = useState(false);
  const [showThumb, setShowThumb] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const [previewPosition, setPreviewPosition] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [availableQualities, setAvailableQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState('auto');
  const [showQualityOptions, setShowQualityOptions] = useState(false);
  const [_videoDimensions, _setVideoDimensions] = useState({ width: 0, height: 0 });
  const [_networkState, _setNetworkState] = useState(0);
  const [_readyState, _setReadyState] = useState(0);

  // Quality definitions with bitrates
  const qualityLevels = {
    '144p': { height: 144, bitrate: 100000, label: '144p (100kbps)' },
    '240p': { height: 240, bitrate: 250000, label: '240p (250kbps)' },
    '360p': { height: 360, bitrate: 500000, label: '360p (500kbps)' },
    '480p': { height: 480, bitrate: 800000, label: '480p (800kbps)' },
    '720p': { height: 720, bitrate: 1500000, label: '720p (1.5Mbps)' },
    '1080p': { height: 1080, bitrate: 3000000, label: '1080p (3Mbps)' },
    '1440p': { height: 1440, bitrate: 6000000, label: '1440p (6Mbps)' },
    '2160p': { height: 2160, bitrate: 12000000, label: '2160p (12Mbps)' },
  };

  // Generate quality URLs based on original video URL
  // Note: This assumes quality variants exist with naming pattern: baseName_quality.extension
  // Currently, the backend doesn't generate these variants, so only the original video is available
  // TODO: Implement backend video transcoding to generate multiple quality variants
  const generateQualityUrls = (originalUrl) => {
    const baseUrl = originalUrl.replace(/\.[^/.]+$/, ''); // Remove extension
    const extension = originalUrl.split('.').pop();

    const qualityUrls = {};
    Object.keys(qualityLevels).forEach(quality => {
      qualityUrls[quality] = `${baseUrl}_${quality}.${extension}`;
    });

    return qualityUrls;
  };

  const [qualityUrls, setQualityUrls] = useState({});

  // Speed options
  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  // Format time (seconds to HH:MM:SS)
  const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    return [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0'),
    ].join(':');
  };

  const [lastSkipTime, setLastSkipTime] = useState(0);

  const skipForward = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const now = Date.now();
    if (now - lastSkipTime < 200) return;

    setLastSkipTime(now);
    videoRef.current.currentTime += 5;
    setCurrentTime(videoRef.current.currentTime);
  };

  // Handle play/pause
  const togglePlay = () => {
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);

      if (currentTime === 0 && duration >= 5 && typeof onPlayStart === 'function') {
        onPlayStart();
      }
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    videoRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  // Handle mute toggle
  const toggleMute = () => {
    if (isMuted) {
      videoRef.current.volume = volume;
    } else {
      videoRef.current.volume = 0;
    }
    setIsMuted(!isMuted);
  };

  // Handle progress bar click
  const handleProgressBarClick = (e) => {
    if (!progressBarRef.current) return;

    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();

    const clientX = e.type.startsWith('touch')
      ? (e.touches && e.touches[0] ? e.touches[0].clientX : 0)
      : e.clientX;

    const offsetX = clientX - rect.left;
    const progressBarWidth = rect.width;
    const seekPercentage = Math.max(0, Math.min(1, offsetX / progressBarWidth));
    const seekTime = seekPercentage * duration;

    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleTimeUpdate = () => {
    const currentTime = videoRef.current.currentTime;
    setCurrentTime(currentTime);
    if (onTimeUpdate) {
      onTimeUpdate(currentTime);
    }
  };

  // Handle loaded metadata
  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    const duration = video.duration;

    setDuration(duration);
    setIsLoading(false);
    setLoadingProgress(100);

    if (typeof onMetadataLoaded === 'function') {
      onMetadataLoaded(duration);
    }

    if (duration >= 5) {
      // Video is ready
    } else {
      console.warn('Video too short (<5s), not playing.');
      setIsPlaying(false);
      video.pause();
    }
  };

  // Skip backward 5 seconds
  const skipBackward = (e) => {
    e.stopPropagation();
    e.preventDefault();
    videoRef.current.currentTime -= 5;
    setCurrentTime(videoRef.current.currentTime);
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Toggle expanded view
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle fullscreen change
  const handleFullscreenChange = () => {
    const fullscreenElement = !!document.fullscreenElement;
    setIsFullscreen(fullscreenElement);
    if (!fullscreenElement && isExpanded) {
      setIsExpanded(true);
    }
  };

  // Change playback speed
  const changePlaybackRate = (rate) => {
    videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSpeedOptions(false);
  };

  // Change brightness
  const handleBrightnessChange = (e) => {
    const newBrightness = parseInt(e.target.value);
    setBrightness(newBrightness);
    videoRef.current.style.filter = `brightness(${newBrightness}%)`;
  };

  // Show controls temporarily
  const showControlsTemporarily = () => {
    setShowControls(true);
    if (controlsTimeout) clearTimeout(controlsTimeout);
    setControlsTimeout(setTimeout(() => setShowControls(false), 3000));
  };

  // Keyboard shortcuts
  const handleKeyDown = (e) => {
    const activeTag = document.activeElement.tagName.toLowerCase();
    const isTyping = activeTag === 'input' || activeTag === 'textarea';

    if (isTyping) return;

    switch (e.key) {
    case ' ':
      e.preventDefault();
      togglePlay();
      break;
    case 'ArrowRight':
      skipForward(e);
      break;
    case 'ArrowLeft':
      skipBackward(e);
      break;
    case 'ArrowUp':
      setVolume((v) => Math.min(v + 0.1, 1));
      break;
    case 'ArrowDown':
      setVolume((v) => Math.max(v - 0.1, 0));
      break;
    case 'f':
      toggleFullscreen();
      break;
    case 'm':
      toggleMute();
      break;
    case 'e':
      toggleExpanded();
      break;
    case 'l':
      toggleLoopMode();
      break;
    case 'k':
      setShowKeyboardHints((prev) => !prev);
      break;
    default:
      break;
    }
  };

  // Set initial position when the video is ready
  useEffect(() => {
    const setInitialPosition = () => {
      if (videoRef.current && initialPosition > 0) {
        videoRef.current.currentTime = initialPosition;
        setCurrentTime(initialPosition);
      }
    };

    if (videoRef.current) {
      if (videoRef.current.readyState >= 2) {
        setInitialPosition();
      } else {
        const handleCanPlay = () => {
          setInitialPosition();
          videoRef.current.removeEventListener('canplay', handleCanPlay);
        };
        videoRef.current.addEventListener('canplay', handleCanPlay);
        return () => {
          if (videoRef.current) {
            videoRef.current.removeEventListener('canplay', handleCanPlay);
          }
        };
      }
    }
  }, [videoUrl, initialPosition]);

  // Set up event listeners
  useEffect(() => {
    const container = containerRef.current;

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    container.addEventListener('mousemove', showControlsTemporarily);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      container.removeEventListener('mousemove', showControlsTemporarily);
      document.removeEventListener('keydown', handleKeyDown);
      if (controlsTimeout) clearTimeout(controlsTimeout);
    };
  }, [controlsTimeout, volume, isExpanded, handleFullscreenChange, showControlsTemporarily, handleKeyDown]);

  // Get size class based on prop and expanded state
  const getSizeClass = () => {
    if (isExpanded && !isFullscreen) return 'evp-expanded';

    switch (size) {
    case 'large':
      return 'evp-large';
    case 'small':
      return 'evp-small';
    default:
      return '';
    }
  };

  // Toggle loop mode
  const toggleLoopMode = () => {
    setLoopMode(prevMode => {
      const newMode = prevMode === 'next' ? 'repeat' :
        prevMode === 'repeat' ? 'none' : 'next';

      setShowLoopMessage(true);
      setTimeout(() => setShowLoopMessage(false), 2000);

      return newMode;
    });
  };

  // Custom video ended handler based on loop mode
  const handleVideoEnded = () => {
    switch (loopMode) {
    case 'repeat':
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      break;
    case 'next':
      if (onEnded) onEnded();
      break;
    case 'none':
      break;
    default:
      if (onEnded) onEnded();
    }
  };

  // Add an effect to detect video source changes and autoplay if needed
  useEffect(() => {
    const shouldAutoplay = sessionStorage.getItem('autoplay_video') === 'true';

    if (shouldAutoplay && videoRef.current) {
      sessionStorage.removeItem('autoplay_video');

      const playTimer = setTimeout(() => {
        videoRef.current.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(err => {
            console.error('Auto-play failed:', err);
            setIsPlaying(false);
          });
      }, 500);

      return () => clearTimeout(playTimer);
    }
  }, [videoUrl]);

  // Add a useEffect to handle auto-play when video URL changes
  useEffect(() => {
    if (videoRef.current && autoPlay) {
      const handleCanPlay = () => {
        videoRef.current.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(err => {
            console.error('Auto-play failed:', err);
          });

        videoRef.current.removeEventListener('canplay', handleCanPlay);
      };

      if (videoRef.current.readyState >= 3) {
        handleCanPlay();
      } else {
        videoRef.current.addEventListener('canplay', handleCanPlay);
        return () => {
          if (videoRef.current) {
            videoRef.current.removeEventListener('canplay', handleCanPlay);
          }
        };
      }
    }
  }, [videoUrl, autoPlay]);

  // Add handler for starting drag operation
  const handleDragStart = (e) => {
    e.preventDefault();

    if (videoRef.current) {
      setWasPlaying(isPlaying);
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }

    setIsDragging(true);

    if (e.type === 'touchstart') {
      handleDrag(e.touches[0]);
    } else {
      handleDrag(e);
    }

    setShowControls(true);
    if (controlsTimeout) clearTimeout(controlsTimeout);
  };

  // Handler for drag movement
  const handleDrag = (clientEvent) => {
    if (!isDragging || !progressBarRef.current) return;

    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();
    const offsetX = clientEvent.clientX - rect.left;
    const progressBarWidth = rect.width;
    const seekPercentage = Math.max(0, Math.min(1, offsetX / progressBarWidth));
    const seekTime = seekPercentage * duration;

    setPreviewPosition(seekPercentage * 100);
    setPreviewTime(seekTime);
    setShowPreview(true);

    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  // Handler for ending drag operation
  const handleDragEnd = () => {
    setIsDragging(false);

    if (wasPlaying && videoRef.current) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(error => {
        console.error('Error resuming playback:', error);
      });
    }

    showControlsTemporarily();
  };

  // Add mouse event handlers to document for dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        handleDrag(e);
      }
    };

    const handleTouchMove = (e) => {
      if (isDragging && e.touches && e.touches[0]) {
        e.preventDefault();
        handleDrag(e.touches[0]);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        handleDragEnd();
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, duration, wasPlaying]);

  // Add function to handle mouse movement over the progress bar (for preview)
  const handleProgressBarHover = (e) => {
    if (!progressBarRef.current) return;

    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();

    const clientX = e.type.startsWith('touch')
      ? (e.touches && e.touches[0] ? e.touches[0].clientX : 0)
      : e.clientX;

    const offsetX = clientX - rect.left;
    const progressBarWidth = rect.width;

    const previewPct = Math.max(0, Math.min(1, offsetX / progressBarWidth));
    const previewTimeValue = previewPct * duration;

    setPreviewPosition(previewPct * 100);
    setPreviewTime(previewTimeValue);
    setShowPreview(true);
  };

  // Function to toggle Picture-in-Picture mode
  const togglePictureInPicture = async () => {
    if (!document.pictureInPictureElement) {
      try {
        await videoRef.current.requestPictureInPicture();
      } catch (error) {
        console.error('Error enabling Picture-in-Picture:', error);
      }
    } else {
      try {
        await document.exitPictureInPicture();
      } catch (error) {
        console.error('Error exiting Picture-in-Picture:', error);
      }
    }
  };

  // Expose methods to parent via imperative handle
  useImperativeHandle(ref, () => ({
    play: () => {
      if (videoRef.current) {
        videoRef.current.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(err => {
            console.error('Play failed:', err);
          });
      }
    },
    pause: () => {
      if (videoRef.current) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    },
    getCurrentTime: () => videoRef.current ? videoRef.current.currentTime : 0,
    getDuration: () => videoRef.current ? videoRef.current.duration : 0,
    getVideoElement: () => videoRef.current,
  }));

  // Initialize quality URLs when video URL changes
  useEffect(() => {
    if (videoUrl) {
      const urls = generateQualityUrls(videoUrl);
      setQualityUrls(urls);

      // Check available qualities by testing URLs
      checkAvailableQualities(urls);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  // Check available video qualities
  const checkAvailableQualities = async (_urls) => {
    // Since the backend doesn't generate quality variants, 
    // we'll skip the HEAD requests and just set available qualities to empty
    // This prevents 404 errors in the console
    setAvailableQualities([]);
    setCurrentQuality('auto');

    // TODO: When backend video transcoding is implemented, uncomment the code below
    /*
        const qualities = [];
        const promises = [];

        Object.entries(urls).forEach(([quality, url]) => {
            const promise = fetch(url, { method: 'HEAD' })
                .then(response => {
                    if (response.ok) {
                        qualities.push(quality);
                    }
                })
                .catch((error) => {
                    // URL doesn't exist, skip this quality
                    // Don't log errors for missing quality variants as this is expected
                    if (process.env.NODE_ENV === 'development') {
                        // console.log(`Quality variant ${quality} not available for this video`);
                    }
                });
            promises.push(promise);
        });

        try {
            await Promise.all(promises);
        } catch (error) {
            // Handle any unexpected errors
            console.warn('Error checking video qualities:', error);
        }

        // Sort qualities by resolution (lowest to highest)
        qualities.sort((a, b) => qualityLevels[a].height - qualityLevels[b].height);

        setAvailableQualities(qualities);
        setCurrentQuality('auto');
        */
  };

  // Handle quality change
  const handleQualityChange = (quality) => {
    if (quality === currentQuality) return;

    const currentTimeStamp = videoRef.current.currentTime;
    const wasPlaying = !videoRef.current.paused;

    setCurrentQuality(quality);
    setShowQualityOptions(false);

    if (quality === 'auto') {
      // Auto quality - use original URL
      videoRef.current.src = videoUrl;
    } else if (qualityUrls[quality]) {
      // Specific quality
      videoRef.current.src = qualityUrls[quality];
    }

    // Restore playback state
    videoRef.current.addEventListener('loadedmetadata', () => {
      videoRef.current.currentTime = currentTimeStamp;
      if (wasPlaying) {
        videoRef.current.play();
      }
    }, { once: true });
  };

  // Video loading events
  const handleLoadStart = () => {
    setIsLoading(true);
    setLoadingProgress(0);
  };

  const handleProgress = () => {
    const video = videoRef.current;
    if (video.buffered.length > 0) {
      const bufferedEnd = video.buffered.end(video.buffered.length - 1);
      const progress = (bufferedEnd / duration) * 100;
      setLoadingProgress(Math.min(progress, 100));
    }
  };

  const handleCanPlay = () => {
    setIsLoading(false);
    setLoadingProgress(100);
  };

  const handleWaiting = () => {
    setIsLoading(true);
  };

  const handlePlaying = () => {
    setIsLoading(false);
  };

  // Validate videoUrl and handle errors
  useEffect(() => {
    if (!videoUrl) {
      console.error('No video source provided.');
      setIsPlaying(false);
      if (videoRef.current) {
        videoRef.current.src = '';
      }
    }
  }, [videoUrl]);

  return (
    <div
      className={`evp-container ${getSizeClass()}`}
      ref={containerRef}
      onDoubleClick={toggleFullscreen}
    >
      <video
        ref={videoRef}
        src={currentQuality === 'auto' ? videoUrl : qualityUrls[currentQuality]}
        onClick={togglePlay}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleVideoEnded}
        onLoadStart={handleLoadStart}
        onProgress={handleProgress}
        onCanPlay={handleCanPlay}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        style={{ filter: `brightness(${brightness}%)` }}
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="evp-loading-overlay">
          <div className="evp-loading-spinner">
            <FaSpinner className="evp-spinner-icon" />
          </div>
          <div className="evp-loading-text">Loading video...</div>
          <div className="evp-loading-progress">
            <div
              className="evp-loading-progress-bar"
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {showLoopMessage && (
        <div className="evp-mode-indicator">
          {loopMode === 'repeat' ? 'Repeating current video' :
            loopMode === 'next' ? 'Auto-play next video' :
              'Playback stops at end'}
        </div>
      )}

      {/* Controls overlay */}
      <div className={`evp-controls ${showControls ? 'evp-visible' : ''}`}>
        {/* Progress bar */}
        <div
          className={`evp-progress-container ${isDragging ? 'evp-dragging' : ''}`}
          onClick={handleProgressBarClick}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onMouseMove={handleProgressBarHover}
          onMouseEnter={() => {
            setShowThumb(true);
            setShowPreview(true);
          }}
          onMouseLeave={() => {
            if (!isDragging) {
              setShowThumb(false);
              setShowPreview(false);
            }
          }}
          ref={progressBarRef}
        >
          <div
            className="evp-progress-bar"
            style={{ width: `${(currentTime / duration) * 100}%` }}
          ></div>
          <div
            className={`evp-progress-thumb ${(showThumb || isDragging) ? 'evp-thumb-visible' : ''}`}
            style={{ left: `${(currentTime / duration) * 100}%` }}
          ></div>

          {/* Preview time tooltip */}
          {showPreview && (
            <div
              className="evp-time-preview"
              style={{ left: `${previewPosition}%` }}
            >
              {formatTime(previewTime)}
            </div>
          )}
        </div>

        {/* Bottom controls */}
        <div className="evp-controls-bottom">
          <div className="evp-controls-left">
            {/* Play/Pause button - primary control */}
            <button
              className="evp-control-button evp-primary-control"
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
              }}
            >
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>

            {/* Skip backward 5 seconds - primary control */}
            <button
              className="evp-control-button evp-primary-control"
              onClick={skipBackward}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <FaUndoAlt />
            </button>

            {/* Skip forward 5 seconds - primary control */}
            <button
              className="evp-control-button evp-primary-control"
              onClick={skipForward}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <FaRedoAlt />
            </button>

            {/* Previous/Next video buttons - hide on smallest screens */}
            <button
              className="evp-control-buttons evp-secondary-control"
              onClick={onPrevious}
              disabled={!hasPrevious}
            >
              <BiSkipPrevious />
            </button>
            <button
              className="evp-control-buttons evp-secondary-control"
              onClick={onNext}
              disabled={!hasNext}
            >
              <BiSkipNext />
            </button>

            {/* Volume control - moves to second row on mobile */}
            <div className="evp-volume-control">
              <button className="evp-control-button" onClick={toggleMute}>
                {isMuted || volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="evp-volume-slider"
              />
            </div>

            {/* Time display - moves to second row on mobile */}
            <span className="evp-time-display">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="evp-controls-right">
            {/* Picture-in-Picture button */}
            <button
              className="evp-control-button"
              onClick={togglePictureInPicture}
              title="Picture-in-Picture"
            >
              <FaRegWindowRestore />
            </button>

            {/* Playback speed */}
            <div className="evp-speed-control">
              <button
                className="evp-control-button"
                onClick={() => {
                  setShowSpeedOptions(!showSpeedOptions);
                  setShowBrightnessSlider(false);
                  setShowQualityOptions(false);
                }}
              >
                <IoMdSpeedometer />
                <span>{playbackRate}x</span>
              </button>

              {showSpeedOptions && (
                <div className="evp-speed-options">
                  {speedOptions.map(option => (
                    <button
                      key={option}
                      className={`evp-speed-option ${playbackRate === option ? 'evp-active' : ''}`}
                      onClick={() => changePlaybackRate(option)}
                    >
                      {option}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Brightness control */}
            <div className="evp-brightness-control">
              <button
                className="evp-control-button"
                onClick={() => {
                  setShowBrightnessSlider(!showBrightnessSlider);
                  setShowSpeedOptions(false);
                  setShowQualityOptions(false);
                }}
              >
                <MdBrightnessMedium />
              </button>

              {showBrightnessSlider && (
                <div className="evp-brightness-slider-container">
                  <input
                    type="range"
                    min="20"
                    max="100"
                    step="5"
                    value={brightness}
                    onChange={handleBrightnessChange}
                    className="evp-brightness-slider"
                  />
                </div>
              )}
            </div>

            {/* Loop mode toggle button */}
            <button
              className={`evp-control-button ${loopMode === 'repeat' ? 'evp-loop-active evp-loop-repeat' :
                loopMode === 'next' ? 'evp-loop-active evp-loop-next' :
                  'evp-loop-none'
              }`}
              onClick={toggleLoopMode}
              title={
                loopMode === 'repeat' ? 'Repeating current video' :
                  loopMode === 'next' ? 'Auto-play next video' :
                    'No looping'
              }
            >
              {loopMode === 'repeat' ? <MdOutlineRepeatOne /> :
                loopMode === 'next' ? <TbPlayerSkipForward /> :
                  <TbRepeatOff />}
            </button>

            {/* Quality control - only show if multiple qualities are available */}
            {availableQualities.length > 0 && (
              <div className="evp-quality-control">
                <button
                  className="evp-control-button evp-secondary-control"
                  onClick={() => {
                    setShowQualityOptions(!showQualityOptions);
                    setShowSpeedOptions(false);
                    setShowBrightnessSlider(false);
                  }}
                  title="Quality"
                >
                  <FaCog />
                  <span className="evp-quality-label">
                    {currentQuality === 'auto' ? 'Auto' : currentQuality}
                  </span>
                </button>
                {showQualityOptions && (
                  <div className="evp-quality-options">
                    <div
                      className={`evp-quality-option ${currentQuality === 'auto' ? 'evp-active' : ''}`}
                      onClick={() => handleQualityChange('auto')}
                    >
                                            Auto
                      {currentQuality === 'auto' && (
                        <span style={{ marginLeft: '8px' }}>✓</span>
                      )}
                    </div>
                    {availableQualities.map((quality) => (
                      <div
                        key={quality}
                        className={`evp-quality-option ${quality === currentQuality ? 'evp-active' : ''}`}
                        onClick={() => handleQualityChange(quality)}
                      >
                        {qualityLevels[quality].label}
                        {quality === currentQuality && (
                          <span style={{ marginLeft: '8px' }}>✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Fullscreen/Expand view button */}
            <button
              className="evp-control-button"
              onClick={(e) => {
                e.stopPropagation();
                if (isFullscreen) {
                  document.exitFullscreen();
                } else {
                  containerRef.current.requestFullscreen().catch(err => {
                    console.error(`Error attempting to enable fullscreen: ${err.message}`);
                  });
                }
                setIsFullscreen(!isFullscreen);
              }}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard shortcut hints */}
      {showKeyboardHints && (
        <div className="evp-keyboard-hints">
          <div className="evp-hint-item"><span>Space</span> Play/Pause</div>
          <div className="evp-hint-item"><span>← →</span> Seek 5s</div>
          <div className="evp-hint-item"><span>↑ ↓</span> Volume</div>
          <div className="evp-hint-item"><span>F</span> Fullscreen</div>
          <div className="evp-hint-item"><span>E</span> Expand</div>
          <div className="evp-hint-item"><span>M</span> Mute</div>
          <div className="evp-hint-item"><span>L</span> Loop Mode</div>
          <div className="evp-hint-item"><span>K</span> Hide hints</div>
        </div>
      )}
    </div>
  );
});

EnhancedVideoPlayer.displayName = 'EnhancedVideoPlayer';

EnhancedVideoPlayer.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  onEnded: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  hasNext: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  initialPosition: PropTypes.number,
  size: PropTypes.string,
  onPlayStart: PropTypes.func,
  onMetadataLoaded: PropTypes.func,
  autoPlay: PropTypes.bool,
  onFirstPlay: PropTypes.func,
};

export default EnhancedVideoPlayer;